import { useLocation } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { useUserAuth } from "@/contexts/UserAuthContext";
import { LocalizedLink, LocalizedNavLink, useLocalizedNav } from "@/i18n/LocalizedLink";
import { Globe, Menu, X, Gamepad2, LogOut, Library, ChevronDown } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { SUPPORTED_LANGS } from "@/i18n/translations";

export default function Navbar() {
  const { t, lang, setLang } = useLang();
  const { user, logout } = useUserAuth();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const langRef = useRef(null);
  const userRef = useRef(null);
  const loc = useLocation();
  const lnav = useLocalizedNav();

  useEffect(() => {
    const onClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
      if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (loc.pathname.startsWith("/admin")) return null;

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/games", label: t("nav.games") },
    { to: "/verify", label: t("nav.verify") },
    { to: "/faq", label: t("nav.faq") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <header className="sticky top-0 z-50 backdrop-blur-2xl bg-black/60 border-b border-white/10" data-testid="site-navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between gap-4">
        <LocalizedLink to="/" className="flex items-center gap-2.5 group" data-testid="brand-logo">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-brand to-brand-cyan flex items-center justify-center glow-blue group-hover:scale-105 transition-transform">
            <Gamepad2 className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-bold text-lg tracking-tight">KHA GAMES</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-brand-cyan/80">Instant Delivery</div>
          </div>
        </LocalizedLink>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <LocalizedNavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) => `px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive ? "text-white bg-white/5" : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`}
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
            >
              {l.label}
            </LocalizedNavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {/* Language switcher */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen((v) => !v)}
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium text-zinc-300 hover:text-white hover:bg-white/5 transition-colors"
              data-testid="lang-toggle"
            >
              <Globe className="w-4 h-4" />
              <span>{lang.toUpperCase()}</span>
              <ChevronDown className="w-3 h-3" />
            </button>
            {langOpen && (
              <div className="absolute end-0 mt-2 min-w-[140px] bg-[#0c0c0c] border border-white/10 rounded-lg shadow-2xl overflow-hidden z-50">
                {SUPPORTED_LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => { setLang(l.code); setLangOpen(false); }}
                    className={`w-full text-start px-4 py-2 text-sm hover:bg-white/5 ${lang === l.code ? "text-brand-cyan bg-brand/5" : "text-zinc-300"}`}
                    data-testid={`lang-${l.code}`}
                  >
                    {l.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* User menu */}
          {user ? (
            <div className="relative" ref={userRef}>
              <button
                onClick={() => setUserOpen((v) => !v)}
                className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 text-sm font-medium border border-white/10"
                data-testid="user-menu-trigger"
              >
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-brand to-brand-cyan flex items-center justify-center text-[10px] font-bold uppercase overflow-hidden">
                  {user.avatar_url ? (
                    <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    user.username[0]
                  )}
                </div>
                <span className="hidden sm:inline max-w-[100px] truncate">{user.username}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              {userOpen && (
                <div className="absolute end-0 mt-2 w-56 bg-[#0c0c0c] border border-white/10 rounded-lg shadow-2xl overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-white/5">
                    <div className="font-semibold text-sm">{user.username}</div>
                    <div className="text-xs text-zinc-500 truncate">{user.email}</div>
                  </div>
                  <LocalizedLink to="/profile" onClick={() => setUserOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-white/5" data-testid="user-menu-profile">
                    <Library className="w-4 h-4" /> {t("nav.library")}
                  </LocalizedLink>
                  <button
                    onClick={() => { logout(); setUserOpen(false); lnav("/"); }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 text-start"
                    data-testid="user-menu-logout"
                  >
                    <LogOut className="w-4 h-4" /> {t("nav.logout")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="hidden sm:flex items-center gap-2">
              <LocalizedLink to="/signin" className="text-sm text-zinc-300 hover:text-white px-3 py-2" data-testid="nav-signin">{t("nav.sign_in")}</LocalizedLink>
              <LocalizedLink to="/signup" className="bg-brand hover:bg-brand-hover text-white font-semibold px-4 py-2 rounded-lg text-sm transition-all" data-testid="nav-signup">{t("nav.sign_up")}</LocalizedLink>
            </div>
          )}

          <button className="md:hidden text-white p-2" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-white/10 bg-black/95 px-6 py-4 flex flex-col gap-1" data-testid="mobile-menu">
          {links.map((l) => (
            <LocalizedNavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              onClick={() => setOpen(false)}
              className={({ isActive }) => `px-3 py-3 rounded text-sm ${isActive ? "text-white bg-white/5" : "text-zinc-400"}`}
            >
              {l.label}
            </LocalizedNavLink>
          ))}
          {!user && (
            <>
              <LocalizedLink to="/signin" onClick={() => setOpen(false)} className="px-3 py-3 text-sm text-zinc-300">{t("nav.sign_in")}</LocalizedLink>
              <LocalizedLink to="/signup" onClick={() => setOpen(false)} className="px-3 py-3 text-sm font-semibold text-brand-cyan">{t("nav.sign_up")}</LocalizedLink>
            </>
          )}
          <div className="border-t border-white/5 mt-2 pt-2 flex gap-2 flex-wrap">
            {SUPPORTED_LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => { setLang(l.code); setOpen(false); }}
                className={`px-3 py-1.5 rounded text-xs font-semibold border ${lang === l.code ? "text-brand-cyan border-brand/40 bg-brand/5" : "text-zinc-400 border-white/10"}`}
                data-testid={`mobile-lang-${l.code}`}
              >{l.name}</button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
